import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import AppCard from "@/components/base/AppCard";
import DownloadModal from "@/components/base/DownloadModal";
import EmptyState from "@/components/base/EmptyState";
import Breadcrumb from "@/components/base/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { watchApps } from "@/mocks/movies";
import type { WatchApp } from "@/mocks/movies";

const categoryOptions = ["全部", "免费", "部分付费", "会员制"];
const platformOptions = ["全部", "iOS", "Android", "TV", "Web", "Windows", "Mac"];
const PAGE_STEP = 4;

export default function AppIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category");
  const [category, setCategory] = useState(
    initialCategory && categoryOptions.includes(initialCategory) ? initialCategory : "全部"
  );
  const [platform, setPlatform] = useState("全部");
  const [visible, setVisible] = useState(PAGE_STEP);
  const [downloadApp, setDownloadApp] = useState<WatchApp | null>(null);

  usePageMeta({
    title: "热门看片APP推荐 - 看片免费app网站",
    description:
      "看片免费app网站整理热门看片APP推荐榜，覆盖免费、部分付费与会员制工具，支持手机、平板与电视大屏，提供功能简介与下载说明。",
    keywords: "看片APP,看片app推荐,免费看片app,影视APP下载,电视看片软件,看片免费app网站",
  });

  useEffect(() => {
    if (initialCategory && categoryOptions.includes(initialCategory)) {
      setCategory(initialCategory);
    }
  }, [initialCategory]);

  useEffect(() => {
    setVisible(PAGE_STEP);
  }, [category, platform]);

  const filtered = useMemo(
    () =>
      watchApps.filter((app) => {
        if (category !== "全部" && app.category !== category) return false;
        if (platform !== "全部" && !app.platforms.includes(platform)) return false;
        return true;
      }),
    [category, platform]
  );

  const handleCategory = (value: string) => {
    setCategory(value);
    if (value === "全部") {
      setSearchParams({});
    } else {
      setSearchParams({ category: value });
    }
  };

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-page mx-auto px-4 md:px-8 py-8 md:py-11">
          <Breadcrumb dark items={[{ label: "首页", to: "/" }, { label: "看片APP推荐" }]} />
          <div className="mt-5 flex flex-col md:flex-row md:items-end justify-between gap-5">
            <div className="max-w-2xl">
              <span className="text-[11px] tracking-[0.22em] text-accent-400 font-label uppercase">Watch Apps</span>
              <h1 className="mt-2 text-2xl md:text-3xl font-bold text-background-50">热门看片APP推荐</h1>
              <p className="mt-2 text-sm leading-6 text-primary-200">
                按收费方式与支持平台筛选，手机、平板、电视盒子都能找到合适的看片工具。
                应用资料每日整理，下载入口为演示状态。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 md:gap-5 shrink-0">
              {[
                { value: String(watchApps.length), label: "收录应用" },
                { value: "4.6", label: "平均评分" },
                { value: "8", label: "支持平台" },
              ].map((stat) => (
                <div key={stat.label} className="text-center md:text-left">
                  <p className="text-lg md:text-xl font-bold text-accent-400 font-label">{stat.value}</p>
                  <p className="text-[11px] text-primary-300">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-6">
        <div className="rounded-lg border border-background-200/80 bg-background-50 px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4 py-1.5">
            <span className="text-xs text-foreground-500 md:w-16 shrink-0 md:pt-1.5">收费方式</span>
            <div className="flex flex-wrap gap-1.5 md:gap-2">
              {categoryOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleCategory(option)}
                  className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    category === option
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-100 hover:text-primary-600"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4 py-1.5 border-t border-background-100 mt-1 pt-3">
            <span className="text-xs text-foreground-500 md:w-16 shrink-0 md:pt-1.5">支持平台</span>
            <div className="flex flex-wrap gap-1.5 md:gap-2">
              {platformOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setPlatform(option)}
                  className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    platform === option
                      ? "bg-secondary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-100 hover:text-primary-600"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-foreground-600">
            共找到 <strong className="text-foreground-900 font-semibold">{filtered.length}</strong> 款应用
          </p>
          {(category !== "全部" || platform !== "全部") && (
            <button
              type="button"
              onClick={() => {
                handleCategory("全部");
                setPlatform("全部");
              }}
              className="text-xs text-foreground-500 hover:text-primary-600 inline-flex items-center gap-1 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon="ri-apps-line"
            title="没有符合条件的看片应用"
            description="当前的收费方式与平台组合暂无结果，试试只保留一个筛选条件，或者查看全部应用。"
            primaryLabel="查看全部应用"
            primaryTo="/app"
          />
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
              {filtered.slice(0, visible).map((app) => (
                <AppCard key={app.id} app={app} onDownload={setDownloadApp} />
              ))}
            </div>

            {visible < filtered.length && (
              <div className="mt-8 flex flex-col items-center gap-3">
                <button
                  type="button"
                  onClick={() => setVisible((count) => count + PAGE_STEP)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer"
                >
                  <i className="ri-loader-4-line"></i>
                  加载更多应用
                </button>
                <p className="text-xs text-foreground-400">
                  已展示 {Math.min(visible, filtered.length)} / {filtered.length} 款
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-12">
        <div className="rounded-lg bg-primary-50 border border-primary-100 p-5 md:p-6">
          <h2 className="text-base font-semibold text-primary-900">如何选择合适的看片工具</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: "ri-smartphone-line", title: "按设备选", desc: "手机优先看安装包大小与离线缓存，电视优先看遥控器操作与投屏。" },
              { icon: "ri-wallet-3-line", title: "按预算选", desc: "免费工具足够日常使用，需要稳定高清线路再考虑会员制应用。" },
              { icon: "ri-shield-check-line", title: "按安全选", desc: "优先从应用商店或开发者官网下载，避免来源不明的安装包。" },
            ].map((tip) => (
              <div key={tip.title} className="flex gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-primary-500 shrink-0">
                  <i className={`${tip.icon} text-lg`}></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{tip.title}</p>
                  <p className="mt-1 text-xs leading-6 text-foreground-600">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DownloadModal
        open={Boolean(downloadApp)}
        appName={downloadApp?.name ?? ""}
        onClose={() => setDownloadApp(null)}
      />
    </SiteLayout>
  );
}